import { useState, useEffect } from 'react';
import { X, BookOpen } from 'lucide-react';

interface Ad {
  id: number;
  title: string;
  description?: string;
  banner_url: string;
  redirect_url: string;
  placement: 'dashboard_top' | 'chorcaghor_top' | 'popup_login' | 'practice_sidebar' | 'leaderboard_bottom';
  device: 'all' | 'desktop' | 'mobile';
}

interface SyllabusItem {
  id: number;
  title: string;
  content: string;
  classes: string[];
  subjects: string[];
  thumbnail: string;
}

interface AlertBannerProps {
  onClose?: () => void;
}

export default function AlertBanner({ onClose }: AlertBannerProps) {
  const [ads, setAds] = useState<Ad[]>([]);
  const [syllabus, setSyllabus] = useState<SyllabusItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkWidth = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkWidth();
    window.addEventListener('resize', checkWidth);

    const fetchData = async () => {
      try {
        const response = await fetch('/wp-json/shuddhoshikha/v1/syllabus-ads');
        const data = await response.json();
        if (data.success) {
          setAds(data.ads || []);
          setSyllabus(data.syllabus || []);
        }
      } catch (err) {
        console.error('Failed to load alert banner payload:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  if (loading) {
    return (
      <div className="w-full h-32 rounded-2xl bg-gray-150 dark:bg-white/5 animate-pulse flex items-center justify-center">
        <span className="font-bangla text-xs text-gray-400">অপেক্ষা করুন...</span>
      </div>
    );
  }

  const topAds = ads.filter(ad => {
    if (ad.placement !== 'dashboard_top' && ad.placement !== 'chorcaghor_top') return false;
    if (ad.device === 'desktop' && isMobile) return false;
    if (ad.device === 'mobile' && !isMobile) return false;
    return true;
  });

  if (topAds.length > 0) {
    const activeAd = topAds[Math.floor(Math.random() * topAds.length)];
    if (activeAd.banner_url) {
      return (
        <div className="relative w-full overflow-hidden rounded-2xl group border border-gray-100/50 dark:border-white/5 shadow-sm">
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-3 right-3 text-white/70 hover:text-white bg-black/40 hover:bg-black/60 p-1.5 rounded-full transition-all duration-200 z-20 cursor-pointer shadow-sm"
              aria-label="Dismiss banner"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <a
            href={activeAd.redirect_url}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full"
          >
            <img
              src={activeAd.banner_url}
              alt={activeAd.title}
              className="w-full h-auto object-cover max-h-[160px] md:max-h-[220px]"
            />
          </a>
        </div>
      );
    } else {
      return (
        <div className="relative w-full overflow-hidden rounded-2xl p-6 md:p-8 bg-gradient-to-r from-sky-400 via-blue-600 to-indigo-900 shadow-[0_10px_25px_rgba(37,99,235,0.15)] border border-white/15 flex flex-col items-center">
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/60 hover:text-white bg-white/0 hover:bg-white/10 p-1.5 rounded-full transition-all duration-200 z-20 cursor-pointer"
              aria-label="Dismiss banner"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          
          <div className="absolute -left-10 -top-10 w-36 md:w-48 h-36 md:h-48 rounded-full bg-cyan-300/30 blur-3xl pointer-events-none" />
          <div className="absolute -right-10 -bottom-10 w-40 md:w-52 h-40 md:h-52 rounded-full bg-indigo-400/35 blur-3xl pointer-events-none" />
          
          <div className="w-36 md:w-44 h-1.5 md:h-2 bg-white/20 backdrop-blur-md border border-white/10 rounded-full overflow-hidden relative shadow-[inset_0_1px_3px_rgba(0,0,0,0.2)] z-10">
            <div className="absolute top-0 bottom-0 left-0 bg-gradient-to-r from-sky-300 via-white to-sky-300 rounded-full shadow-[0_0_12px_rgba(255,255,255,0.9)] w-[40%] animate-pulse" />
          </div>

          <h2 className="font-bangla text-xl md:text-2xl font-bold text-white text-center mt-4 tracking-wide leading-tight z-10 drop-shadow-[0_2px_4px_rgba(0,0,0,0.15)]">
            {activeAd.title}
          </h2>

          {activeAd.description && (
            <p className="font-bangla text-xs md:text-sm font-medium text-blue-100/90 text-center mt-2 z-10 drop-shadow-[0_1px_2px_rgba(0,0,0,0.1)]">
              {activeAd.description}
            </p>
          )}

          <div className="mt-4 md:mt-5 z-10">
            <a
              href={activeAd.redirect_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3.5 py-1 bg-white/10 backdrop-blur-md border border-white/15 rounded-full text-[11px] md:text-xs font-bangla text-white/90 shadow-sm transition-all hover:bg-white/15 cursor-pointer no-underline"
            >
              <span className="tracking-wide">শুদ্ধশিক্ষা</span>
            </a>
          </div>
        </div>
      );
    }
  }

  if (syllabus.length > 0) {
    const latestSyllabus = syllabus[0];
    return (
      <div className="relative w-full overflow-hidden rounded-2xl p-6 md:p-8 bg-gradient-to-r from-teal-500 via-emerald-600 to-cyan-900 shadow-md border border-white/15 flex flex-col md:flex-row items-center justify-between gap-6 text-left">
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white bg-white/0 hover:bg-white/10 p-1.5 rounded-full transition-all duration-200 z-20 cursor-pointer"
            aria-label="Dismiss banner"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center text-white flex-shrink-0 shadow-inner">
            <BookOpen className="w-6 h-6" />
          </div>
          <div>
            <span className="font-bangla text-[10px] md:text-xs font-bold px-2 py-0.5 bg-white/25 text-white rounded-full uppercase tracking-wider">সিলেবাস আপডেট</span>
            <h3 className="font-bangla text-lg font-bold text-white mt-1.5 leading-tight">{latestSyllabus.title}</h3>
            <p className="font-bangla text-xs md:text-sm text-teal-50 mt-1 max-w-xl leading-relaxed">{latestSyllabus.content}</p>
          </div>
        </div>

        {latestSyllabus.thumbnail && (
          <img 
            src={latestSyllabus.thumbnail} 
            alt={latestSyllabus.title} 
            className="w-20 h-20 object-cover rounded-xl border border-white/10 shadow-sm hidden md:block flex-shrink-0" 
          />
        )}
      </div>
    );
  }

  return null;
}
